import { RCS_BLOCKED_USERS_URL, RUS_URL, buildServiceParamMap, performGet } from "../../common";
import { userInfo } from "../../common/user-info";
import jwtInterceptor from "../../common/jwt-interceptor";

// User-to-user blocks, backed by rcs /blocked_users. Every action is
// defensive: if the endpoint isn't deployed yet (404 / gateway error) the
// store simply reports nobody as blocked and block/unblock surface a notice
// instead of throwing, so the messaging UI keeps working unchanged.

const state = {
  // { [blockedUserId]: blockRecordId } for the current user's own blocks
  blockIdsByUserId: {},
  loaded: false,
  // false once a request has shown the backend isn't there yet
  available: true,
  // Bottom-right toast ("User successfully blocked"); null when hidden.
  toast: null
};

let toastTimer = null;

const getters = {
  isUserBlocked: (state) => (userId) => {
    return userId != null && state.blockIdsByUserId[parseInt(userId)] != null;
  },
  blockedUserIds: (state) => Object.keys(state.blockIdsByUserId).map(id => parseInt(id))
};

const actions = {
  async fetchBlockedUsers({ commit }) {
    const user = userInfo();
    if (!user || !user.user_id) { return; }
    try {
      const response = await jwtInterceptor({
        method: "GET",
        url: RCS_BLOCKED_USERS_URL(),
        headers: { "Content-Type": "application/json" },
        params: buildServiceParamMap({ blocking_user_id: user.user_id, "page[size]": 500 })
      });
      const rows = (response?.data?.data || []);
      const map = {};
      rows.forEach(r => {
        const blockedId = parseInt(r.attributes?.["blocked-user-id"]);
        if (!isNaN(blockedId)) { map[blockedId] = r.id; }
      });
      commit("setBlockIdsByUserId", map);
      commit("setAvailable", true);
    } catch (e) {
      console.log("blocked_users fetch failed (backend may not be deployed yet)", e?.response?.status);
      commit("setBlockIdsByUserId", {});
      commit("setAvailable", false);
    } finally {
      commit("setLoaded", true);
    }
  },

  showToast({ commit }, message) {
    commit("setToast", message);
    if (toastTimer) { clearTimeout(toastTimer); }
    toastTimer = setTimeout(() => commit("setToast", null), 4000);
  },

  hideToast({ commit }) {
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    commit("setToast", null);
  },

  // Hydrate name/avatar for every blocked user into user/usersById (the
  // Blocked Users screen can't rely on rally membership fetches, since a
  // blocked user may not share any currently-loaded rally).
  async fetchBlockedUserProfiles({ state, commit, rootState }) {
    const ids = Object.keys(state.blockIdsByUserId)
      .filter(id => !(rootState.user.usersById || {})[id]);
    if (ids.length === 0) { return; }
    try {
      const response = await performGet({ id: ids, include: "avatar", "page[size]": 100 }, RUS_URL(), userInfo());
      const users = Array.isArray(response?.data?.data) ? response.data.data : [response.data.data].filter(Boolean);
      commit("user/setUsersById", { users }, { root: true });
      if (response?.data?.included) {
        this.dispatch("contents/setIncludedById", { included: response.data.included, avatarType: "user" });
      }
    } catch (e) {
      console.log("fetchBlockedUserProfiles failed", e?.response?.status);
    }
  },

  // userId: who to block; contentUid: the message that prompted it (optional)
  async blockUser({ commit, dispatch }, { userId, contentUid }) {
    const user = userInfo();
    if (!user || !user.user_id || userId == null) { return false; }
    try {
      const response = await jwtInterceptor({
        method: "POST",
        url: RCS_BLOCKED_USERS_URL(),
        headers: { "Content-Type": "application/json" },
        data: {
          data: {
            // rh_base validates this against resource_name.pluralize.dasherize
            type: "blocked-users",
            attributes: {
              blocking_user_id: user.user_id,
              blocked_user_id: userId,
              content_uid: contentUid
            }
          }
        }
      });
      const id = response?.data?.data?.id;
      if (id) {
        commit("setBlock", { userId, blockId: id });
        commit("setAvailable", true);
        dispatch("showToast", "User successfully blocked");
        return true;
      }
      throw new Error("no id in response");
    } catch (e) {
      console.log("blockUser failed", e?.response?.status);
      dispatch("user/flashNotice", "Blocking isn't available right now. Please try again later.", { root: true });
      return false;
    }
  },

  async unblockUser({ state, commit, dispatch }, { userId }) {
    const blockId = state.blockIdsByUserId[parseInt(userId)];
    if (!blockId) { return false; }
    try {
      await jwtInterceptor({
        method: "DELETE",
        url: RCS_BLOCKED_USERS_URL() + "/" + blockId + ".json",
        headers: { "Content-Type": "application/json" }
      });
      commit("removeBlock", { userId });
      dispatch("showToast", "User successfully unblocked");
      return true;
    } catch (e) {
      console.log("unblockUser failed", e?.response?.status);
      dispatch("user/flashNotice", "Unblocking isn't available right now. Please try again later.", { root: true });
      return false;
    }
  }
};

const mutations = {
  setBlockIdsByUserId(state, map) {
    state.blockIdsByUserId = { ...map };
  },
  setBlock(state, { userId, blockId }) {
    state.blockIdsByUserId = { ...state.blockIdsByUserId, [parseInt(userId)]: blockId };
  },
  removeBlock(state, { userId }) {
    const next = { ...state.blockIdsByUserId };
    delete next[parseInt(userId)];
    state.blockIdsByUserId = next;
  },
  setLoaded(state, loaded) {
    state.loaded = loaded;
  },
  setAvailable(state, available) {
    state.available = available;
  },
  setToast(state, message) {
    state.toast = message;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
